// Specialized error boundaries for different user types and contexts

import React, { Component, ReactNode } from 'react';
import type { ErrorInfo } from 'react';
import { classifyError, logError, type AppError } from '../utils/errorHandling';
import ErrorDisplay from './ErrorDisplay';

interface ErrorBoundaryState {
  hasError: boolean;
  error: AppError | null;
  errorId: string | null;
  retryCount: number;
}

interface BaseErrorBoundaryProps {
  children: ReactNode;
  onError?: (error: AppError, errorInfo: ErrorInfo) => void;
  maxRetries?: number;
  fallbackComponent?: (error: AppError, retry: () => void, errorId: string) => ReactNode;
}

// Base error boundary class
abstract class BaseErrorBoundary extends Component<BaseErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: BaseErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorId: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const appError = classifyError(error);
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error: appError,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const appError = classifyError(error);
    
    // Log the error with context
    logError(appError, this.getErrorContext());
    
    // Call custom error handler if provided
    this.props.onError?.(appError, errorInfo);
    
    // Log React-specific information
    console.error(`${this.getErrorContext()} Error Boundary caught an error:`, {
      error: appError,
      errorInfo,
      componentStack: errorInfo.componentStack,
    });
  }

  componentWillUnmount(): void {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  retry = (): void => {
    const maxRetries = this.props.maxRetries || 3;
    
    if (this.state.retryCount >= maxRetries) {
      console.warn(`Maximum retry attempts (${maxRetries}) reached for ${this.getErrorContext()}`);
      return;
    }

    this.setState(prev => ({
      hasError: false,
      error: null,
      errorId: null,
      retryCount: prev.retryCount + 1,
    }));
  };

  // Abstract method to be implemented by subclasses
  abstract getErrorContext(): string;
  abstract renderFallback(error: AppError, errorId: string): ReactNode;

  render(): ReactNode {
    if (this.state.hasError && this.state.error && this.state.errorId) {
      // Use custom fallback if provided
      if (this.props.fallbackComponent) {
        return this.props.fallbackComponent(this.state.error, this.retry, this.state.errorId);
      }

      // Use subclass-specific fallback
      return this.renderFallback(this.state.error, this.state.errorId);
    }

    return this.props.children;
  }
}

// Guest user error boundary
interface GuestErrorBoundaryProps extends BaseErrorBoundaryProps {
  sessionId?: string;
}

export class GuestErrorBoundary extends BaseErrorBoundary {
  declare props: GuestErrorBoundaryProps;

  getErrorContext(): string {
    return `Guest Session ${this.props.sessionId || 'unknown'}`;
  }

  renderFallback(error: AppError, errorId: string): ReactNode {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
        <div className="bg-navy-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="bg-red-500/15 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Vigikus Fatigue Check</h2>
            <p className="text-sm text-navy-200">Guest Session Error</p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-white mb-2">
              Something went wrong
            </h3>
            <p className="text-navy-200 mb-4">
              {error.userMessage}
            </p>
            {error.code && (
              <p className="text-xs text-navy-300 mb-4">
                Error Code: {error.code}
              </p>
            )}
          </div>

          <div className="space-y-3">
            {error.retryable && this.state.retryCount < (this.props.maxRetries || 3) && (
              <button
                onClick={this.retry}
                className="w-full bg-gold-500 hover:bg-gold-600 text-navy-950 py-2 px-4 rounded-md font-medium"
              >
                Try Again ({this.state.retryCount + 1}/{this.props.maxRetries || 3})
              </button>
            )}
            
            <button
              onClick={() => window.location.href = '/'}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md font-medium"
            >
              Start New Test
            </button>
            
            <button
              onClick={() => window.location.reload()}
              className="w-full border border-navy-700 text-navy-100 py-2 px-4 rounded-md font-medium hover:bg-navy-900"
            >
              Refresh Page
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-navy-300">
              Error ID: {errorId}
            </p>
            {this.props.sessionId && (
              <p className="text-xs text-navy-300">
                Session: {this.props.sessionId}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

// Regular user error boundary
interface UserErrorBoundaryProps extends BaseErrorBoundaryProps {
  userId?: string;
  userName?: string;
}

export class UserErrorBoundary extends BaseErrorBoundary {
  declare props: UserErrorBoundaryProps;

  getErrorContext(): string {
    return `User Dashboard ${this.props.userId || 'unknown'}`;
  }

  renderFallback(error: AppError, errorId: string): ReactNode {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
        <div className="bg-navy-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="bg-red-500/15 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Vigikus Fatigue Check</h2>
            <p className="text-sm text-navy-200">
              {this.props.userName ? `Welcome, ${this.props.userName}` : 'User Dashboard Error'}
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-white mb-2">
              Dashboard Error
            </h3>
            <p className="text-navy-200 mb-4">
              {error.userMessage}
            </p>
            {error.code && (
              <p className="text-xs text-navy-300 mb-4">
                Error Code: {error.code}
              </p>
            )}
          </div>

          <div className="space-y-3">
            {error.retryable && this.state.retryCount < (this.props.maxRetries || 3) && (
              <button
                onClick={this.retry}
                className="w-full bg-gold-500 hover:bg-gold-600 text-navy-950 py-2 px-4 rounded-md font-medium"
              >
                Retry Loading ({this.state.retryCount + 1}/{this.props.maxRetries || 3})
              </button>
            )}
            
            <button
              onClick={() => window.location.href = '/user/dashboard'}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md font-medium"
            >
              Return to Dashboard
            </button>
            
            <button
              onClick={() => window.location.href = '/logout'}
              className="w-full border border-navy-700 text-navy-100 py-2 px-4 rounded-md font-medium hover:bg-navy-900"
            >
              Logout & Login Again
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-navy-300">
              Error ID: {errorId}
            </p>
            {this.props.userId && (
              <p className="text-xs text-navy-300">
                User ID: {this.props.userId}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

// Admin user error boundary
interface AdminErrorBoundaryProps extends BaseErrorBoundaryProps {
  adminId?: string;
  adminName?: string;
  companyId?: string;
}

export class AdminErrorBoundary extends BaseErrorBoundary {
  declare props: AdminErrorBoundaryProps;

  getErrorContext(): string {
    return `Admin Dashboard ${this.props.adminId || 'unknown'}`;
  }

  renderFallback(error: AppError, errorId: string): ReactNode {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
        <div className="bg-navy-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="bg-red-500/15 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Vigikus Fatigue Check</h2>
            <p className="text-sm text-navy-200">
              {this.props.adminName ? `Admin: ${this.props.adminName}` : 'Admin Dashboard Error'}
            </p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-white mb-2">
              Admin Dashboard Error
            </h3>
            <p className="text-navy-200 mb-4">
              {error.userMessage}
            </p>
            {error.code && (
              <p className="text-xs text-navy-300 mb-4">
                Error Code: {error.code}
              </p>
            )}
          </div>

          <div className="space-y-3">
            {error.retryable && this.state.retryCount < (this.props.maxRetries || 3) && (
              <button
                onClick={this.retry}
                className="w-full bg-gold-500 hover:bg-gold-600 text-navy-950 py-2 px-4 rounded-md font-medium"
              >
                Retry Loading ({this.state.retryCount + 1}/{this.props.maxRetries || 3})
              </button>
            )}
            
            <button
              onClick={() => window.location.href = '/admin/dashboard'}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md font-medium"
            >
              Return to Admin Dashboard
            </button>
            
            <button
              onClick={() => window.location.href = '/user/dashboard'}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-4 rounded-md font-medium"
            >
              Switch to User View
            </button>
            
            <button
              onClick={() => window.location.href = '/logout'}
              className="w-full border border-navy-700 text-navy-100 py-2 px-4 rounded-md font-medium hover:bg-navy-900"
            >
              Logout & Login Again
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-navy-300">
              Error ID: {errorId}
            </p>
            {this.props.adminId && (
              <p className="text-xs text-navy-300">
                Admin ID: {this.props.adminId}
              </p>
            )}
            {this.props.companyId && (
              <p className="text-xs text-navy-300">
                Company: {this.props.companyId}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

// Test interface error boundary
interface TestErrorBoundaryProps extends BaseErrorBoundaryProps {
  testId?: string;
  userType: 'admin' | 'guest' | 'kiosk';
  onTestRestart?: () => void;
}

export class TestErrorBoundary extends BaseErrorBoundary {
  declare props: TestErrorBoundaryProps;

  getErrorContext(): string {
    return `Test Interface ${this.props.testId || 'unknown'} (${this.props.userType})`;
  }

  renderFallback(error: AppError, errorId: string): ReactNode {
    const getTitle = () => {
      switch (this.props.userType) {
        case 'guest':
          return 'Fatigue Check';
        case 'admin':
          return 'Admin Test Interface';
        case 'kiosk':
          return 'Kiosk Test';
        default:
          return 'Test Interface';
      }
    };

    const getReturnPath = () => {
      switch (this.props.userType) {
        case 'guest':
          return '/';
        case 'admin':
          return '/admin/dashboard';
        case 'kiosk':
          return '/kiosk/start';
        default:
          return '/';
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
        <div className="bg-navy-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="bg-red-500/15 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">{getTitle()}</h2>
            <p className="text-sm text-navy-200">Test Interface Error</p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-white mb-2">
              Test Error
            </h3>
            <p className="text-navy-200 mb-4">
              {error.type === 'timing' 
                ? 'There was an issue with the test timing system. This may affect result accuracy.'
                : error.userMessage
              }
            </p>
            {error.code && (
              <p className="text-xs text-navy-300 mb-4">
                Error Code: {error.code}
              </p>
            )}
          </div>

          <div className="space-y-3">
            {this.props.onTestRestart && (
              <button
                onClick={this.props.onTestRestart}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md font-medium"
              >
                Restart Test
              </button>
            )}
            
            {error.retryable && this.state.retryCount < (this.props.maxRetries || 3) && (
              <button
                onClick={this.retry}
                className="w-full bg-gold-500 hover:bg-gold-600 text-navy-950 py-2 px-4 rounded-md font-medium"
              >
                Try Again ({this.state.retryCount + 1}/{this.props.maxRetries || 3})
              </button>
            )}
            
            <button
              onClick={() => window.location.href = getReturnPath()}
              className="w-full border border-navy-700 text-navy-100 py-2 px-4 rounded-md font-medium hover:bg-navy-900"
            >
              Return to Dashboard
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-navy-300">
              Error ID: {errorId}
            </p>
            {this.props.testId && (
              <p className="text-xs text-navy-300">
                Test ID: {this.props.testId}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

// Authentication error boundary
export class AuthErrorBoundary extends BaseErrorBoundary {
  getErrorContext(): string {
    return 'Authentication System';
  }

  renderFallback(error: AppError, errorId: string): ReactNode {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
        <div className="bg-navy-800 rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="bg-red-500/15 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Vigikus Fatigue Check</h2>
            <p className="text-sm text-navy-200">Authentication Error</p>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium text-white mb-2">
              Login System Error
            </h3>
            <p className="text-navy-200 mb-4">
              {error.userMessage}
            </p>
            {error.code && (
              <p className="text-xs text-navy-300 mb-4">
                Error Code: {error.code}
              </p>
            )}
          </div>

          <div className="space-y-3">
            {error.retryable && this.state.retryCount < (this.props.maxRetries || 3) && (
              <button
                onClick={this.retry}
                className="w-full bg-gold-500 hover:bg-gold-600 text-navy-950 py-2 px-4 rounded-md font-medium"
              >
                Try Again ({this.state.retryCount + 1}/{this.props.maxRetries || 3})
              </button>
            )}
            
            <button
              onClick={() => window.location.href = '/login'}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md font-medium"
            >
              Go to Login
            </button>
            
            <button
              onClick={() => window.location.href = '/'}
              className="w-full border border-navy-700 text-navy-100 py-2 px-4 rounded-md font-medium hover:bg-navy-900"
            >
              Take Test as Guest
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-navy-300">
              Error ID: {errorId}
            </p>
          </div>
        </div>
      </div>
    );
  }
}
